<template>
  <form @submit.prevent="saveFormaPagamento" class="formas-pagamento-form">
    <div class="flex-container">
      <!-- Payment Method Name -->
      <div class="flex-item-half">
        <div class="form-group" :class="{ 'has-error': errors.forma_pagamento_nome }">
          <label for="forma_pagamento_nome">
            {{ t('Nome da forma de pagamento') }} <span class="text-danger">*</span>
          </label>
          <input-text
            elem-id="forma_pagamento_nome"
            v-model="formData.forma_pagamento_nome"
            :placeholder="t('Nome da forma de pagamento')"
            data-testid="formas-pagamento-input-nome"
          />
          <div v-if="errors.forma_pagamento_nome" class="invalid-feedback">
            {{ errors.forma_pagamento_nome }}
          </div>
        </div>
      </div>

      <!-- Payment Method Code -->
      <div class="flex-item-half">
        <div class="form-group" :class="{ 'has-error': errors.forma_pagamento_codigo }">
          <label for="forma_pagamento_codigo">
            {{ t('Código') }}
          </label>
          <select-list
            elem-id="forma_pagamento_codigo"
            v-model="codigoModel"
            :itens-select="codigoOptions"
            :placeholder="t('Selecione o código')"
            data-testid="formas-pagamento-select-codigo"
          />
          <div v-if="errors.forma_pagamento_codigo" class="invalid-feedback">
            {{ errors.forma_pagamento_codigo }}
          </div>
        </div>
      </div>
    </div>

    <div class="flex-container">
      <!-- Has Fees -->
      <div class="flex-item-half">
        <div class="form-group" :class="{ 'has-error': errors.forma_pagamento_possui_taxas }">
          <label for="forma_pagamento_possui_taxas">
            {{ t('Possui taxas?') }} <span class="text-danger">*</span>
          </label>
          <select-list
            elem-id="forma_pagamento_possui_taxas"
            v-model="formData.forma_pagamento_possui_taxas"
            :itens-select="possuiTaxasOptions"
            data-testid="formas-pagamento-select-possui-taxas"
          />
          <div v-if="errors.forma_pagamento_possui_taxas" class="invalid-feedback">
            {{ errors.forma_pagamento_possui_taxas }}
          </div>
        </div>
      </div>

      <!-- Accepts Installments -->
      <div class="flex-item-half">
        <div class="form-group" :class="{ 'has-error': errors.forma_pagamento_aceita_parcelado }">
          <label for="forma_pagamento_aceita_parcelado">
            {{ t('Aceita parcelar?') }} <span class="text-danger">*</span>
          </label>
          <select-list
            elem-id="forma_pagamento_aceita_parcelado"
            v-model="formData.forma_pagamento_aceita_parcelado"
            :itens-select="aceitaParceladoOptions"
            data-testid="formas-pagamento-select-aceita-parcelado"
          />
          <div v-if="errors.forma_pagamento_aceita_parcelado" class="invalid-feedback">
            {{ errors.forma_pagamento_aceita_parcelado }}
          </div>
        </div>
      </div>
    </div>

    <!-- Parcelamento (chamado 2026-0067): máximo de parcelas e em quantos dias a primeira cai na conta -->
    <div class="flex-container">
      <div class="flex-item-half" v-if="formData.forma_pagamento_aceita_parcelado === '1'">
        <div class="form-group" :class="{ 'has-error': errors.forma_pagamento_parcelas_max }">
          <label for="forma_pagamento_parcelas_max">{{ t('Máximo de parcelas') }}</label>
          <input-number
            elem-id="forma_pagamento_parcelas_max"
            v-model="formData.forma_pagamento_parcelas_max"
            :placeholder="t('12 (padrão)')"
            :min="1"
            :max="48"
            data-testid="formas-pagamento-input-parcelas-max"
          />
          <small class="field-hint">{{ t('Em branco: até 12 parcelas.') }}</small>
          <div v-if="errors.forma_pagamento_parcelas_max" class="invalid-feedback">
            {{ errors.forma_pagamento_parcelas_max }}
          </div>
        </div>
      </div>
      <div class="flex-item-half">
        <div class="form-group" :class="{ 'has-error': errors.forma_pagamento_dias_para_receber }">
          <label for="forma_pagamento_dias_para_receber">{{ t('Dias para receber') }}</label>
          <input-number
            elem-id="forma_pagamento_dias_para_receber"
            v-model="formData.forma_pagamento_dias_para_receber"
            :placeholder="t('0')"
            :min="0"
            :max="365"
            data-testid="formas-pagamento-input-dias-para-receber"
          />
          <small class="field-hint">{{ t('Em quantos dias o valor cai na conta. Parcelado: a primeira parcela; as seguintes, a cada 30 dias.') }}</small>
          <div v-if="errors.forma_pagamento_dias_para_receber" class="invalid-feedback">
            {{ errors.forma_pagamento_dias_para_receber }}
          </div>
        </div>
      </div>
    </div>

    <div class="flex-container" v-if="showTaxaMensal || showTaxaTransacao">
      <!-- Monthly Fee (conditional) -->
      <div class="flex-item-half" v-if="showTaxaMensal">
        <div class="form-group" :class="{ 'has-error': errors.forma_pagamento_taxa_mensal }">
          <label for="forma_pagamento_taxa_mensal">
            {{ t('Taxa mensal') }} (%) <span class="text-danger">*</span>
          </label>
          <input-number
            elem-id="forma_pagamento_taxa_mensal"
            v-model="formData.forma_pagamento_taxa_mensal"
            :placeholder="t('Taxa mensal')"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :min="0"
            :max="100"
            data-testid="formas-pagamento-input-taxa-mensal"
          />
          <div v-if="errors.forma_pagamento_taxa_mensal" class="invalid-feedback">
            {{ errors.forma_pagamento_taxa_mensal }}
          </div>
        </div>
      </div>

      <!-- Transaction Fee (conditional) -->
      <div class="flex-item-half" v-if="showTaxaTransacao">
        <div class="form-group" :class="{ 'has-error': errors.forma_pagamento_taxa_transacao }">
          <label for="forma_pagamento_taxa_transacao">
            {{ t('Taxa por transação') }} <span class="text-danger">*</span>
          </label>
          <input-number
            elem-id="forma_pagamento_taxa_transacao"
            v-model="formData.forma_pagamento_taxa_transacao"
            :placeholder="t('Taxa por transação')"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :min="0"
            data-testid="formas-pagamento-input-taxa-transacao"
          />
          <div v-if="errors.forma_pagamento_taxa_transacao" class="invalid-feedback">
            {{ errors.forma_pagamento_taxa_transacao }}
          </div>
        </div>
      </div>
    </div>

    <div class="flex-container" v-if="showTaxaTransacao">
      <!-- Transaction Fee Type (conditional) -->
      <div class="flex-item-full">
        <div class="form-group" :class="{ 'has-error': errors.forma_pagamento_taxa_transacao_tipo }">
          <label for="forma_pagamento_taxa_transacao_tipo">
            {{ t('Tipo da taxa de transação') }} <span class="text-danger">*</span>
          </label>
          <select-list
            elem-id="forma_pagamento_taxa_transacao_tipo"
            v-model="formData.forma_pagamento_taxa_transacao_tipo"
            :itens-select="tipoTaxaOptions"
            data-testid="formas-pagamento-select-tipo-taxa"
          />
          <div v-if="errors.forma_pagamento_taxa_transacao_tipo" class="invalid-feedback">
            {{ errors.forma_pagamento_taxa_transacao_tipo }}
          </div>
        </div>
      </div>
    </div>

    <!-- Loading indicator -->
    <div v-if="isLoading" class="alert alert-info" data-testid="formas-pagamento-loading">
      <ProgressSpinner style="width: 1rem; height: 1rem" strokeWidth="4" /> {{ t('Carregando...') }}
    </div>

    <!-- Error display -->
    <div v-if="generalError" class="alert alert-danger" data-testid="formas-pagamento-error">
      {{ generalError }}
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from '../../inputs/InputText.vue';
import InputNumber from '../../inputs/InputNumber.vue';
import SelectList from '../../inputs/SelectList.vue';
import { eventBus } from '../../eventBus';
import { useFormasPagamento } from '../../composables/useFormasPagamento';
import { PARCELAS_MAX_PADRAO } from '../../composables/useParcelamento';
import { log } from '../../utils/DebugLogger';

type FormaPagamentoId = number | string;

interface FormaPagamentoData {
  forma_pagamento_id: FormaPagamentoId;
  forma_pagamento_nome: string;
  forma_pagamento_codigo: string | null;
  forma_pagamento_possui_taxas: string;
  forma_pagamento_aceita_parcelado: string;
  forma_pagamento_taxa_mensal: number;
  forma_pagamento_taxa_transacao: number;
  forma_pagamento_taxa_transacao_tipo: string;
  /** null = o limite padrão (12) */
  forma_pagamento_parcelas_max: number | null;
  forma_pagamento_dias_para_receber: number;
}

interface FormErrors {
  forma_pagamento_nome?: string;
  forma_pagamento_codigo?: string;
  forma_pagamento_possui_taxas?: string;
  forma_pagamento_aceita_parcelado?: string;
  forma_pagamento_taxa_mensal?: string;
  forma_pagamento_taxa_transacao?: string;
  forma_pagamento_taxa_transacao_tipo?: string;
  forma_pagamento_parcelas_max?: string;
  forma_pagamento_dias_para_receber?: string;
}

interface SelectOption {
  ID: string;
  Name: string;
}

const props = withDefaults(defineProps<{
  isEditMode?: boolean;
}>(), {
  isEditMode: false
});

const emit = defineEmits<{
  'save-success': [];
  'delete-success': [];
}>();

// Get i18n composable
const { t } = useI18n();

// Get formas pagamento composable
const {
  buscarFormaPagamento,
  criarFormaPagamento,
  atualizarFormaPagamento,
  deletarFormaPagamento
} = useFormasPagamento();

// Reactive data
const formData = ref<FormaPagamentoData>({
  forma_pagamento_id: 0,
  forma_pagamento_nome: '',
  forma_pagamento_codigo: null,
  forma_pagamento_possui_taxas: '0',
  forma_pagamento_aceita_parcelado: '0',
  forma_pagamento_taxa_mensal: 0,
  forma_pagamento_taxa_transacao: 0,
  forma_pagamento_taxa_transacao_tipo: '1',
  forma_pagamento_parcelas_max: null,
  forma_pagamento_dias_para_receber: 0
});

const errors = ref<FormErrors>({});
const isLoading = ref<boolean>(false);
const generalError = ref<string>('');

// Dropdown options ({ID, Name} shape required by select-list wrapper)
const possuiTaxasOptions = computed<SelectOption[]>(() => [
  { ID: '0', Name: t('Não') },
  { ID: '1', Name: t('Sim - mensal') },
  { ID: '2', Name: t('Sim - por transação') },
  { ID: '3', Name: t('Sim - mensal e por transação') }
]);

const aceitaParceladoOptions = computed<SelectOption[]>(() => [
  { ID: '0', Name: t('Não') },
  { ID: '1', Name: t('Sim') }
]);

const tipoTaxaOptions = computed<SelectOption[]>(() => [
  { ID: '1', Name: t('Percentual') },
  { ID: '2', Name: t('Fixo por transação') }
]);

// Payment method code options
const codigoOptions = computed<SelectOption[]>(() => [
  { ID: 'CASH', Name: t('Dinheiro') },
  { ID: 'PIX', Name: t('PIX') },
  { ID: 'CREDIT_CARD', Name: t('Cartão de Crédito') },
  { ID: 'DEBIT_CARD', Name: t('Cartão de Débito') },
  { ID: 'BANK_TRANSFER', Name: t('Transferência Bancária') },
  { ID: 'BOLETO', Name: t('Boleto') },
  { ID: 'REWORK', Name: t('Retrabalho') },
  { ID: 'BARTER', Name: t('Permuta') },
  { ID: 'CHECK', Name: t('Cheque') }
]);

// select-list v-model accepts string | number, but we keep null in state to mean "no code"
const codigoModel = computed<string>({
  get: () => formData.value.forma_pagamento_codigo ?? '',
  set: (value: string | number) => {
    formData.value.forma_pagamento_codigo = value === '' ? null : String(value);
  }
});

// Computed properties
const canSave = computed<boolean>(() => {
  return formData.value.forma_pagamento_nome.trim() !== '' && !isLoading.value;
});

const showTaxaMensal = computed<boolean>(() => {
  const value = parseInt(formData.value.forma_pagamento_possui_taxas);
  return value === 1 || value === 3;
});

const showTaxaTransacao = computed<boolean>(() => {
  const value = parseInt(formData.value.forma_pagamento_possui_taxas);
  return value === 2 || value === 3;
});

// Watchers
watch(() => formData.value.forma_pagamento_possui_taxas, (newValue: string) => {
  // Reset fee fields when possui_taxas changes
  const value = parseInt(newValue);
  if (value === 0) {
    formData.value.forma_pagamento_taxa_mensal = 0;
    formData.value.forma_pagamento_taxa_transacao = 0;
  } else if (value === 1) {
    formData.value.forma_pagamento_taxa_transacao = 0;
  } else if (value === 2) {
    formData.value.forma_pagamento_taxa_mensal = 0;
  }
});

// Methods
const initializeForNew = (): void => {
  log('FormasPagamentoForm', 'initializeForNew', 'Initializing form for new payment method');
  resetForm();
};

const loadForEdit = async (formaPagamentoId: FormaPagamentoId): Promise<void> => {
  log('FormasPagamentoForm', 'loadForEdit', 'Loading payment method for edit', formaPagamentoId);
  isLoading.value = true;
  generalError.value = '';
  errors.value = {};

  try {
    const data = await buscarFormaPagamento(Number(formaPagamentoId));
    log('FormasPagamentoForm', 'loadForEdit', 'Loaded data', data);

    if (!data) return;

    formData.value = {
      forma_pagamento_id: data.forma_pagamento_id,
      forma_pagamento_nome: data.forma_pagamento_nome || '',
      forma_pagamento_codigo: data.forma_pagamento_codigo || null,
      forma_pagamento_possui_taxas: String(data.forma_pagamento_possui_taxas || '0'),
      forma_pagamento_aceita_parcelado: String(data.forma_pagamento_aceita_parcelado || '0'),
      forma_pagamento_taxa_mensal: data.forma_pagamento_taxa_mensal || 0,
      forma_pagamento_taxa_transacao: data.forma_pagamento_taxa_transacao || 0,
      forma_pagamento_taxa_transacao_tipo: String(data.forma_pagamento_taxa_transacao_tipo || '1'),
      forma_pagamento_parcelas_max: data.forma_pagamento_parcelas_max ?? null,
      forma_pagamento_dias_para_receber: data.forma_pagamento_dias_para_receber ?? 0
    };

  } catch (error: unknown) {
    const err = error as Error;
    log('FormasPagamentoForm', 'loadForEdit', 'Error', err);
    generalError.value = err.message;
    eventBus.emit('globalMessage.error', t('Erro ao carregar forma de pagamento: ') + err.message);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('FormasPagamentoForm', 'validateForm', 'Validating form');
  errors.value = {};

  // Name is required
  if (!formData.value.forma_pagamento_nome.trim()) {
    errors.value.forma_pagamento_nome = t('Nome da forma de pagamento é obrigatório');
  }

  // Conditional validation for fees
  const possuiTaxas = parseInt(formData.value.forma_pagamento_possui_taxas);

  if (possuiTaxas === 1 || possuiTaxas === 3) {
    if (!formData.value.forma_pagamento_taxa_mensal || formData.value.forma_pagamento_taxa_mensal < 0) {
      errors.value.forma_pagamento_taxa_mensal = t('Taxa mensal é obrigatória');
    }
  }

  if (possuiTaxas === 2 || possuiTaxas === 3) {
    if (!formData.value.forma_pagamento_taxa_transacao || formData.value.forma_pagamento_taxa_transacao < 0) {
      errors.value.forma_pagamento_taxa_transacao = t('Taxa por transação é obrigatória');
    }
    if (!formData.value.forma_pagamento_taxa_transacao_tipo) {
      errors.value.forma_pagamento_taxa_transacao_tipo = t('Tipo da taxa de transação é obrigatório');
    }
  }

  // Parcelamento (chamado 2026-0067) — os mesmos limites do contrato no backend.
  const max = formData.value.forma_pagamento_parcelas_max;
  if (formData.value.forma_pagamento_aceita_parcelado === '1' && max !== null && max !== undefined && String(max) !== ''
      && (Number(max) < 1 || Number(max) > 48)) {
    errors.value.forma_pagamento_parcelas_max = t('O máximo de parcelas deve estar entre 1 e 48');
  }
  const dias = Number(formData.value.forma_pagamento_dias_para_receber ?? 0);
  if (dias < 0 || dias > 365) {
    errors.value.forma_pagamento_dias_para_receber = t('Os dias para receber devem estar entre 0 e 365');
  }

  const isValid = Object.keys(errors.value).length === 0;
  log('FormasPagamentoForm', 'validateForm', 'Validation result', isValid, errors.value);
  return isValid;
};

const saveFormaPagamento = async (): Promise<void> => {
  log('FormasPagamentoForm', 'saveFormaPagamento', 'Attempting to save', formData.value);

  if (!validateForm()) {
    eventBus.emit('globalMessage.error', t('Por favor, corrija os erros no formulário'));
    return;
  }

  isLoading.value = true;
  generalError.value = '';

  try {
    const data = {
      forma_pagamento_nome: formData.value.forma_pagamento_nome.trim(),
      forma_pagamento_codigo: formData.value.forma_pagamento_codigo || undefined,
      forma_pagamento_possui_taxas: parseInt(formData.value.forma_pagamento_possui_taxas),
      forma_pagamento_aceita_parcelado: parseInt(formData.value.forma_pagamento_aceita_parcelado),
      forma_pagamento_taxa_mensal: parseFloat(String(formData.value.forma_pagamento_taxa_mensal)) || 0,
      forma_pagamento_taxa_transacao: parseFloat(String(formData.value.forma_pagamento_taxa_transacao)) || 0,
      forma_pagamento_taxa_transacao_tipo: parseInt(formData.value.forma_pagamento_taxa_transacao_tipo),
      // Em branco = o padrão (12), enviado explícito para que a edição não mantenha um máximo
      // antigo. Só vale se a forma parcela.
      forma_pagamento_parcelas_max: formData.value.forma_pagamento_aceita_parcelado === '1'
        ? (Number(formData.value.forma_pagamento_parcelas_max) || PARCELAS_MAX_PADRAO)
        : undefined,
      forma_pagamento_dias_para_receber: Number(formData.value.forma_pagamento_dias_para_receber) || 0
    };

    if (props.isEditMode) {
      log('FormasPagamentoForm', 'saveFormaPagamento', 'Updating existing payment method', formData.value.forma_pagamento_id);
      await atualizarFormaPagamento(formData.value.forma_pagamento_id, data);
      eventBus.emit('globalMessage.success', t('Forma de pagamento atualizada com sucesso!'));
    } else {
      log('FormasPagamentoForm', 'saveFormaPagamento', 'Creating new payment method');
      await criarFormaPagamento(data);
      eventBus.emit('globalMessage.success', t('Forma de pagamento criada com sucesso!'));
    }

    // Emit events
    eventBus.emit('formas-pagamento.saved');
    emit('save-success');

  } catch (error: unknown) {
    const err = error as Error;
    log('FormasPagamentoForm', 'saveFormaPagamento', 'Error', err);
    generalError.value = err.message;
    eventBus.emit('globalMessage.error', t('Erro ao salvar forma de pagamento: ') + err.message);
  } finally {
    isLoading.value = false;
  }
};

const deleteFormaPagamento = async (): Promise<void> => {
  log('FormasPagamentoForm', 'deleteFormaPagamento', 'Attempting to delete', formData.value.forma_pagamento_id);

  if (!formData.value.forma_pagamento_id) {
    eventBus.emit('globalMessage.error', t('ID da forma de pagamento não encontrado'));
    return;
  }

  isLoading.value = true;
  generalError.value = '';

  try {
    await deletarFormaPagamento(Number(formData.value.forma_pagamento_id));
    log('FormasPagamentoForm', 'deleteFormaPagamento', 'Deleted successfully');
    eventBus.emit('globalMessage.success', t('Forma de pagamento excluída com sucesso!'));

    // Emit events
    eventBus.emit('formas-pagamento.deleted');
    emit('delete-success');

  } catch (error: unknown) {
    const err = error as Error;
    log('FormasPagamentoForm', 'deleteFormaPagamento', 'Error', err);
    generalError.value = err.message;
    eventBus.emit('globalMessage.error', t('Erro ao excluir forma de pagamento: ') + err.message);
  } finally {
    isLoading.value = false;
  }
};

const resetForm = (): void => {
  log('FormasPagamentoForm', 'resetForm', 'Resetting form');
  formData.value = {
    forma_pagamento_id: 0,
    forma_pagamento_nome: '',
    forma_pagamento_codigo: null,
    forma_pagamento_possui_taxas: '0',
    forma_pagamento_aceita_parcelado: '0',
    forma_pagamento_taxa_mensal: 0,
    forma_pagamento_taxa_transacao: 0,
    forma_pagamento_taxa_transacao_tipo: '1',
    forma_pagamento_parcelas_max: null,
    forma_pagamento_dias_para_receber: 0
  };
  errors.value = {};
  generalError.value = '';
};

const getFormaPagamentoData = (): FormaPagamentoData => {
  return formData.value;
};

// Expose methods for parent components
defineExpose({
  canSave,
  initializeForNew,
  loadForEdit,
  saveFormaPagamento,
  deleteFormaPagamento,
  resetForm,
  getFormaPagamentoData
});
</script>

<style scoped>
.formas-pagamento-form {
  padding: 0;
}

.flex-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.flex-item-half {
  flex: 1 1 calc(50% - 8px);
  min-width: 200px;
}

.flex-item-full {
  flex: 1 1 100%;
}

.w-full {
  width: 100%;
}

.form-group {
  margin-bottom: 15px;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 0.875em;
  margin-top: 0.25rem;
}

.text-danger {
  color: #dc3545;
}

.alert {
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 4px;
}

.alert-info {
  background-color: #d9edf7;
  border-color: #bce8f1;
  color: #31708f;
}

.alert-danger {
  background-color: #f2dede;
  border-color: #ebccd1;
  color: #a94442;
}
.field-hint {
  display: block;
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 4px;
}
</style>
